import React from 'react';
import { cn } from '../../utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightElement?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      id,
      name,
      label,
      error,
      hint,
      leftIcon,
      rightElement,
      ...props
    },
    ref,
  ) => {
    const reactId = React.useId();
    const inputId = id ?? name ?? reactId;
    const hasLeftIcon = Boolean(leftIcon);
    const hasRightElement = Boolean(rightElement);

    return (
      <div className="space-y-1.5">
        {label ? (
          <label htmlFor={inputId} className="block text-sm font-medium text-ink-700">
            {label}
          </label>
        ) : null}
        <div className="relative">
          {hasLeftIcon ? (
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-400">
              {leftIcon}
            </div>
          ) : null}
          <input
            ref={ref}
            id={inputId}
            name={name}
            className={cn(
              'block min-h-10 w-full rounded-lg border bg-white px-3 py-2 text-sm text-ink-900 transition-colors duration-200 placeholder:text-ink-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-500',
              error
                ? 'border-danger-100 focus:border-danger-600 focus:ring-danger-100'
                : 'border-ink-300 focus:border-academy-500 focus:ring-academy-100',
              hasLeftIcon && 'pl-10',
              hasRightElement && 'pr-10',
              className,
            )}
            {...props}
          />
          {hasRightElement ? (
            <div className="absolute inset-y-0 right-3 flex items-center">{rightElement}</div>
          ) : null}
        </div>
        {error ? (
          <p className="text-sm text-danger-600">{error}</p>
        ) : hint ? (
          <p className="text-sm text-ink-500">{hint}</p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = 'Input';
